import { memo, type MouseEventHandler, type PointerEventHandler } from "react";
import {
  ChevronLeftIcon,
  CornerUpRightIcon,
  ListPlusIcon,
  PlayIcon,
  SkipForwardIcon,
  type LucideIcon,
} from "lucide-react";
import type { FollowUpBehavior } from "@amoreg/contracts";
import { cn } from "~/lib/utils";
import {
  COMPOSER_DISPATCH_ACTION_COPY,
  formatComposerDispatchTooltip,
  resolveComposerDispatchMode,
  type ActiveTurnComposerAction,
} from "../../composerDispatch";
import { useShortcutModifierState } from "../../shortcutModifierState";
import { MorphIcon } from "../MorphIcon";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { useComposerFinish } from "./composerFinish";

interface PendingActionState {
  questionIndex: number;
  isLastQuestion: boolean;
  canAdvance: boolean;
  isResponding: boolean;
  isComplete: boolean;
}

interface ComposerPrimaryActionsProps {
  sendLabel?: string;
  stopLabel?: string;
  compact: boolean;
  pendingAction: PendingActionState | null;
  /** A reply is running: sending steers, interrupts or queues instead of starting a turn. */
  isRunning: boolean;
  /** What a click does while a reply runs (the `followUpBehavior` setting). */
  followUpBehavior?: FollowUpBehavior | undefined;
  /** The `composer.sendAlternate` shortcut, named in the send button's tooltip. */
  alternateShortcutLabel?: string | null | undefined;
  isSendBusy: boolean;
  sendDisabledReason: string | null;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  isPreparingWorktree: boolean;
  hasSendableContent: boolean;
  preserveComposerFocusOnPointerDown?: boolean;
  /**
   * Click handler for the send button. It receives the click, so it can read
   * Ctrl/⌘ and send the alternate action; without it the button submits its
   * form and offers no alternate.
   */
  onSubmitMessage?: MouseEventHandler<HTMLButtonElement> | undefined;
  /**
   * Resume an interrupted reply. With it set and nothing to send, the send
   * button becomes Resume thread; absent, there is nothing to resume.
   */
  onResume?: (() => void) | undefined;
  onPreviousPendingQuestion: () => void;
  onInterrupt: () => void;
}

// Adapted from pingdotgg/t3code (MIT), apps/web/src/components/chat/ComposerPrimaryActions.tsx
// at 7202ba666: one round send button whose icon morphs between the follow-up
// actions while a reply runs. Upstream has steer and queue; Amoreg adds
// interrupt (upstream's restart), drawn as a skip-forward: stop, then send.
export const COMPOSER_DISPATCH_ACTION_ICONS: Readonly<
  Record<ActiveTurnComposerAction, LucideIcon>
> = {
  steer: CornerUpRightIcon,
  interrupt: SkipForwardIcon,
  queue: ListPlusIcon,
};

export const formatPendingPrimaryActionLabel = (input: {
  compact: boolean;
  isLastQuestion: boolean;
  isResponding: boolean;
  questionIndex: number;
}) => {
  if (input.isResponding) {
    return "Submitting...";
  }
  if (input.compact) {
    return input.isLastQuestion ? "Submit" : "Next";
  }
  if (!input.isLastQuestion) {
    return "Next question";
  }
  return input.questionIndex > 0 ? "Submit answers" : "Submit answer";
};

const preventPointerFocus: PointerEventHandler<HTMLElement> = (event) => {
  event.preventDefault();
};

export const ComposerPrimaryActions = memo(function ComposerPrimaryActions({
  compact,
  sendLabel,
  stopLabel,
  pendingAction,
  isRunning,
  followUpBehavior = "steer",
  alternateShortcutLabel = null,
  isSendBusy,
  sendDisabledReason,
  isConnecting,
  isEnvironmentUnavailable,
  isPreparingWorktree,
  hasSendableContent,
  preserveComposerFocusOnPointerDown = false,
  onSubmitMessage,
  onResume,
  onPreviousPendingQuestion,
  onInterrupt,
}: ComposerPrimaryActionsProps) {
  const fine = useComposerFinish() === "fine";
  const pointerFocusProps = preserveComposerFocusOnPointerDown
    ? { onPointerDown: preventPointerFocus }
    : undefined;
  const isSendDisabled = sendDisabledReason !== null;
  // Holding Ctrl/⌘ arms the alternate action, so the icon shows what a click
  // will do. Only a click handler can read the modifier; a plain form submit
  // always takes the default.
  const offersAlternate = isRunning && onSubmitMessage !== undefined;
  const shortcutModifiers = useShortcutModifierState();
  const dispatchMode = resolveComposerDispatchMode({
    running: isRunning,
    activeTurnDefault: followUpBehavior,
    alternateModifier: offersAlternate && (shortcutModifiers.metaKey || shortcutModifiers.ctrlKey),
  });

  const stopGenerationLabel = stopLabel ?? "Stop generation";
  const renderStopGenerationButton = (insidePendingAction: boolean) => (
    <Tooltip key="interrupt">
      <TooltipTrigger
        render={
          <button
            type="button"
            className={cn(
              "flex cursor-pointer items-center justify-center rounded-full bg-destructive/90 text-white shadow-xs shadow-destructive/24 inset-shadow-[0_1px_--theme(--color-white/16%)] transition-all duration-150 hover:bg-destructive hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none [&_svg]:pointer-events-none",
              insidePendingAction ? "size-8 sm:size-7" : "size-8 sm:h-8 sm:w-8",
            )}
            {...pointerFocusProps}
            onClick={onInterrupt}
            aria-label={stopGenerationLabel}
          />
        }
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
          <rect x="2" y="2" width="8" height="8" rx="1.5" />
        </svg>
      </TooltipTrigger>
      <TooltipPopup>{stopGenerationLabel}</TooltipPopup>
    </Tooltip>
  );

  if (pendingAction) {
    return (
      <div className={cn("flex items-center justify-end", compact ? "gap-1.5" : "gap-2")}>
        {isRunning ? renderStopGenerationButton(true) : null}
        {pendingAction.questionIndex > 0 ? (
          compact ? (
            <Button
              size="icon-sm"
              variant="outline"
              className="rounded-full"
              {...pointerFocusProps}
              onClick={onPreviousPendingQuestion}
              disabled={pendingAction.isResponding}
              aria-label="Previous question"
            >
              <ChevronLeftIcon className="size-3.5" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              {...pointerFocusProps}
              onClick={onPreviousPendingQuestion}
              disabled={pendingAction.isResponding}
            >
              Previous
            </Button>
          )
        ) : null}
        <Button
          type="submit"
          size="sm"
          className={cn(
            "rounded-full bg-message-action text-message-action-foreground hover:bg-message-action-hover",
            compact ? "px-3" : "px-4",
          )}
          {...pointerFocusProps}
          disabled={
            isEnvironmentUnavailable ||
            pendingAction.isResponding ||
            (pendingAction.isLastQuestion ? !pendingAction.isComplete : !pendingAction.canAdvance)
          }
        >
          {formatPendingPrimaryActionLabel({
            compact,
            isLastQuestion: pendingAction.isLastQuestion,
            isResponding: pendingAction.isResponding,
            questionIndex: pendingAction.questionIndex,
          })}
        </Button>
      </div>
    );
  }

  // While a reply runs with nothing to send, the button is Stop.
  if (isRunning && !hasSendableContent) {
    return renderStopGenerationButton(false);
  }

  // Adapted from pingdotgg/t3code (MIT) at de3439142: after an interrupted
  // reply, the empty composer's send button resumes the thread.
  const showResume = onResume !== undefined && !isRunning && !hasSendableContent;
  const runningAction: ActiveTurnComposerAction | null =
    showResume || dispatchMode === "auto" ? null : dispatchMode;
  const submitLabel = showResume
    ? "Resume thread"
    : runningAction
      ? COMPOSER_DISPATCH_ACTION_COPY[runningAction].ariaLabel
      : "Send message";
  const submitStatus = isEnvironmentUnavailable
    ? "Environment disconnected"
    : (sendDisabledReason ??
      (isConnecting
        ? "Connecting"
        : isPreparingWorktree
          ? "Preparing worktree"
          : isSendBusy
            ? "Sending"
            : null));
  const submitTooltip =
    submitStatus ??
    (showResume
      ? submitLabel
      : offersAlternate
        ? formatComposerDispatchTooltip({
            activeTurnDefault: followUpBehavior,
            alternateShortcutLabel,
          })
        : (sendLabel ?? submitLabel));

  const sendButton = (
    <button
      type={showResume ? "button" : "submit"}
      className={cn(
        "relative isolate flex h-9 w-9 items-center justify-center overflow-hidden rounded-full text-message-action-foreground shadow-xs transition-all duration-150 enabled:cursor-pointer enabled:inset-shadow-[0_1px_--theme(--color-white/16%)] hover:scale-105 active:inset-shadow-[0_1px_--theme(--color-black/8%)] active:shadow-none disabled:pointer-events-none disabled:opacity-30 disabled:shadow-none disabled:hover:scale-100 sm:h-8 sm:w-8 [&_svg]:pointer-events-none",
        "bg-message-action enabled:shadow-message-action/24 hover:bg-message-action-hover",
        fine && "rounded-[7px] transition-[background-color,color,opacity,box-shadow]",
      )}
      {...pointerFocusProps}
      onClick={showResume ? onResume : onSubmitMessage}
      disabled={
        isSendBusy ||
        isSendDisabled ||
        isConnecting ||
        isEnvironmentUnavailable ||
        (!hasSendableContent && !showResume)
      }
      aria-label={
        showResume ? (submitStatus ?? submitLabel) : (sendLabel ?? submitStatus ?? submitLabel)
      }
      data-composer-dispatch={showResume ? "resume" : dispatchMode}
    >
      {isConnecting || isSendBusy ? (
        <Spinner className="size-3.5" aria-hidden="true" />
      ) : showResume ? (
        <PlayIcon className="size-3.5 fill-current" aria-hidden="true" />
      ) : runningAction ? (
        <MorphIcon className="size-4" icon={COMPOSER_DISPATCH_ACTION_ICONS[runningAction]} />
      ) : (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );

  const submit = (
    <Tooltip key="submit">
      <TooltipTrigger render={<span className="inline-flex" />}>{sendButton}</TooltipTrigger>
      <TooltipPopup>{submitTooltip}</TooltipPopup>
    </Tooltip>
  );
  // A composer without the follow-up actions (the Inspector's) has no
  // interrupt to offer, so Stop stays beside its send button while drafting.
  if (isRunning && !offersAlternate) {
    return (
      <div className="flex items-center gap-2">
        {submit}
        {renderStopGenerationButton(false)}
      </div>
    );
  }
  return submit;
});
